import axios from "axios";
import jwt_decode from "jwt-decode";
const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;

export const getJwtToken = () => {
  const jwtToken = JSON.parse(localStorage.getItem("loginDetails"));
  return jwtToken;
};

export const getUserData = () => {
  const jwtToken = JSON.parse(localStorage.getItem("loginDetails"));
  if (jwtToken) {
    const userObject = jwt_decode(jwtToken);
    if (userObject) {
      const camelCaseUserObject = {
        email: userObject.email,
        firstName: userObject.first_name,
        lastName: userObject.last_name,
        role: userObject.role,
        empId: userObject.employee_id,
        tenantId: userObject.tenant_id,
      };

      return camelCaseUserObject;
    }
  } else {
    return {};
  }
};

export const getHeaders = (contentType = "application/json") => {
  const jwtToken = getJwtToken();
  const { tenantId } = getUserData();
  const headers = {
    "Content-Type": contentType,
    Tenantid: `${tenantId}`,
    // "ngrok-skip-browser-warning": "69420",
    Authorization: `bearer ${jwtToken}`,
  };

  return headers;
};

/*
 * RBAC — logged-in user's effective permissions (live).
 *
 * GET /v1/permissions — no employee id in the path; the backend resolves the
 * employee from the JWT itself (see api/routers/permissions.py). Requires the
 * same JWT + Tenantid headers as every other authenticated call.
 *
 * Response is the `CurrentUserPermissions` model returned DIRECTLY (NOT
 * wrapped in the project's usual {status,message,data} envelope):
 *   { "employee_id": "...", "role": "Admin", "permissions": ["dashboard.view", ...] }
 *
 * `permissions` already contains only the feature codes this user is allowed
 * to use (employee overrides already applied on top of the role defaults —
 * see PermissionController.get_effective_permission_codes). No further
 * evaluation is needed on the frontend.
 */
export const getUserPermissions = async () => {
  try {
    if (!getJwtToken()) {
      return [];
    }
    const url = `${backendEndpoint}/v1/permissions`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    return Array.isArray(data?.permissions) ? data.permissions : [];
  } catch (err) {
    return [];
  }
};

/*
 * RBAC — Role Permissions management (Role Permissions admin screen).
 *
 * GET /v1/{employeeId}/rbac/role-permissions/matrix
 *   → { status, message, data: { version, roles, modules, features, permissions } }
 * PUT /v1/{employeeId}/rbac/role-permissions
 *   Body: { version, changes: [{ role_id, feature_id, allowed }] }
 *   → { status, message, data: {...} }
 *
 * role_id / feature_id are opaque UUID strings — never parsed as numbers and
 * always echoed back exactly as received. Both functions return the raw fetch
 * Response (same pattern as the rest of this file); the caller reads
 * response.ok / response.status and parses response.json() itself so it can
 * branch on the project's standard `{status,message,data}` envelope (400/409/500)
 * vs FastAPI's default `{detail}` envelope (401/403).
 */
export const getRolePermissionMatrix = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/role-permissions/matrix`;
    return await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
  } catch (err) {
    throw err;
  }
};

export const saveRolePermissions = async ({ version, changes } = {}) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/role-permissions`;
    return await fetch(url, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({ version, changes }),
    });
  } catch (err) {
    throw err;
  }
};

/*
 * RBAC — Role Management (Role Management list + Permission Configuration
 * page). Distinct from the legacy `getRolePermissionMatrix`/`saveRolePermissions`
 * pair above (which compares every role side-by-side); these operate on ONE
 * role at a time, per the contract in docs/RBAC_ROLE_MANAGEMENT_API_CONTRACT.md:
 *
 * GET    /v1/{employeeId}/rbac/roles
 *   → { status, message, data: { roles: [{ id, name, description, type,
 *       is_system, can_edit, can_delete, can_duplicate, employees_assigned,
 *       created_date }] } }
 * POST   /v1/{employeeId}/rbac/roles                Body: { name, description }
 * PUT    /v1/{employeeId}/rbac/roles/{roleId}        Body: { name, description }
 * DELETE /v1/{employeeId}/rbac/roles/{roleId}
 * POST   /v1/{employeeId}/rbac/roles/{roleId}/duplicate   Body: { name, description }
 *   (roleId = the SOURCE role being copied)
 * GET    /v1/{employeeId}/rbac/roles/{roleId}/permissions
 *   → { status, message, data: { version, role, roles, modules, features, permissions } }
 * PUT    /v1/{employeeId}/rbac/roles/{roleId}/permissions
 *   Body: { version, changes: [{ role_id, feature_id, allowed }] }
 *
 * Same response/error pattern as every other call in this file: callers read
 * response.ok / response.status and parse response.json() themselves.
 */
export const getRoles = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/roles`;
    return await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
  } catch (err) {
    throw err;
  }
};

// Minimal, unrestricted role list — open to any authenticated employee, unlike
// getRoles() (Admin/Super Admin only). Used by the Add/Edit Employee role
// dropdown, which filters on each role's is_assignable_by_current_role flag.
export const getAssignableRoles = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/roles/assignable`;
    return await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
  } catch (err) {
    throw err;
  }
};

export const createRole = async ({
  name,
  description,
  showReportingToRole,
  assignableRoleIds,
} = {}) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/roles`;
    return await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        name,
        description,
        show_reporting_to_role: !!showReportingToRole,
        assignable_role_ids: assignableRoleIds ?? null,
      }),
    });
  } catch (err) {
    throw err;
  }
};

// `showReportingToRole` and `assignableRoleIds` are both optional: omit either
// to leave that setting untouched, matching the endpoint's partial-update
// semantics for every other field. Note an EMPTY assignableRoleIds array is
// meaningful — it clears the role's assignable roles — so it is sent as-is.
export const updateRole = async (
  roleId,
  { name, description, showReportingToRole, assignableRoleIds } = {},
) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/roles/${roleId}`;
    return await fetch(url, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({
        name,
        description,
        ...(showReportingToRole === undefined
          ? {}
          : { show_reporting_to_role: !!showReportingToRole }),
        ...(assignableRoleIds === undefined
          ? {}
          : { assignable_role_ids: assignableRoleIds }),
      }),
    });
  } catch (err) {
    throw err;
  }
};

export const deleteRole = async (roleId) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/roles/${roleId}`;
    return await fetch(url, {
      method: "DELETE",
      headers: getHeaders(),
    });
  } catch (err) {
    throw err;
  }
};

export const duplicateRole = async (roleId, { name, description } = {}) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/roles/${roleId}/duplicate`;
    return await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ name, description }),
    });
  } catch (err) {
    throw err;
  }
};

export const getRolePermissions = async (roleId) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/roles/${roleId}/permissions`;
    return await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
  } catch (err) {
    throw err;
  }
};

export const saveRolePermissionsForRole = async (roleId, { version, changes } = {}) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/rbac/roles/${roleId}/permissions`;
    return await fetch(url, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({ version, changes }),
    });
  } catch (err) {
    throw err;
  }
};

export const authentication = async (loginObject) => {
  try {
    const response = await axios.post(`${backendEndpoint}/v1/authentication`, {
      email: loginObject.email,
      password: loginObject.password,
      device_token: loginObject.deviceToken,
      domain_name: loginObject.domainName,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateDeviceToken = (deviceToken) => {
  try {
    const { empId, email } = getUserData();
    const response = axios.post(
      `${backendEndpoint}/v1/${empId}/device_token`,
      {
        device_token: deviceToken,
        employee_email: email,
        employee_id: empId,
      },
      {
        headers: getHeaders(),
      },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteDeviceToken = (deviceToken) => {
  try {
    const { empId } = getUserData();
    const response = axios.post(
      `${backendEndpoint}/v1/${empId}/logout`,
      {
        device_token: deviceToken,
      },
      {
        headers: getHeaders(),
      },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const verifyUser = async (data) => {
  try {
    const response = await axios.post(
      `${backendEndpoint}/v1/forgot_password`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    if (response.status === 200) {
      sessionStorage.setItem(
        "userResetToken",
        JSON.stringify(response.data.token),
      );
    }
    return response;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const verifyOtp = async (data) => {
  try {
    const userResetToken = JSON.parse(sessionStorage.getItem("userResetToken"));
    const { employee_id, tenant_id } = jwt_decode(userResetToken);
    const response = await axios.post(
      `${backendEndpoint}/v1/${employee_id}/verify_otp`,
      {
        otp: data,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userResetToken}`,
          Tenantid: `${tenant_id}`,
        },
      },
    );

    if (response.status === 200) {
      sessionStorage.setItem(
        "userResetToken",
        JSON.stringify(response.data.token),
      );
    }
    return response;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const updateUserPassword = async (data) => {
  try {
    const userResetToken = JSON.parse(sessionStorage.getItem("userResetToken"));
    const { employee_id, tenant_id } = jwt_decode(userResetToken);
    const response = await axios.post(
      `${backendEndpoint}/v1/${employee_id}/set_password`,
      {
        new_password: data,
      },
      {
        headers: {
          Authorization: `Bearer ${userResetToken}`,
          Tenantid: `${tenant_id}`,
        },
      },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const getNotifications = async () => {
  const { empId } = getUserData();
  const url = `${backendEndpoint}/v1/${empId}/notifications`;

  const options = {
    method: "GET",
    headers: getHeaders(),
  };
  try {
    const response = await fetch(url, options);
    if (response) {
      return response;
    }
  } catch (err) {
    throw err;
  }
};

export const markNotifications = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/notifications`;
    const options = {
      method: "PUT",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteNotifications = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/notifications`;
    const options = {
      method: "DELETE",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const getProjects = async (client_id) => {
  const { empId } = getUserData();
  const params = new URLSearchParams();
  if (client_id) params.append("client_id", client_id);

  const url = `${backendEndpoint}/v1/${empId}/projects${
    params.toString() ? "?" + params.toString() : ""
  }`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getRevenueSummary = async (project_id, start_month, end_month) => {
  const { empId } = getUserData();
  const params = new URLSearchParams();
  if (project_id) params.append("project_id", project_id);
  if (start_month) params.append("start_month", start_month);
  if (end_month) params.append("end_month", end_month);

  const url = `${backendEndpoint}/v1/${empId}/revenue_summary${
    params.toString() ? "?" + params.toString() : ""
  }`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const getProjectRevenue = async (project_id, start_month, end_month) => {
  const { empId } = getUserData();
  const params = new URLSearchParams();
  if (project_id) params.append("project_id", project_id);
  if (start_month) params.append("start_month", start_month);
  if (end_month) params.append("end_month", end_month);

  const url = `${backendEndpoint}/v1/${empId}/project_revenue${
    params.toString() ? "?" + params.toString() : ""
  }`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const getEmployeeRevenue = async (project_id, start_month, end_month) => {
  const { empId } = getUserData();
  const params = new URLSearchParams();
  if (project_id) params.append("project_id", project_id);
  if (start_month) params.append("start_month", start_month);
  if (end_month) params.append("end_month", end_month);

  const url = `${backendEndpoint}/v1/${empId}/employee_revenue${
    params.toString() ? "?" + params.toString() : ""
  }`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};


export const getTenantInvoiceDetails = async () => {
  const { empId } = getUserData();

  const url = `${backendEndpoint}/v1/${empId}/tenant_invoice_details`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const getClients = async () => {
  const { empId } = getUserData();

  const url = `${backendEndpoint}/v1/${empId}/clients`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getWorkLogs = async (year, month, employee_id, project_id) => {
  const { empId } = getUserData();
  const params = new URLSearchParams();
  if (project_id) params.append("project_id", project_id);
  if (employee_id) params.append("emp_id", employee_id);
  if (month) params.append("month", month);
  if (year) params.append("year", year);

  const url = `${backendEndpoint}/v1/${empId}/worklog${
    params.toString() ? "?" + params.toString() : ""
  }`;

  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};
export const getDownloadInvoice = async (invoiceObj) => {
  const { empId } = getUserData();
  const url = `${backendEndpoint}/v1/${empId}/project/${invoiceObj.id}/invoice/pdf?month=${invoiceObj.month}&year=${invoiceObj.year}`;

  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};
export const getProjectsByEmpId = async () => {
  const { empId } = getUserData();

  const url = `${backendEndpoint}/v1/${empId}/projects/employee/${empId}`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const getRevenueProjectSummary = async () => {
  const { empId } = getUserData();

  const url = `${backendEndpoint}/v1/${empId}/dashboard/summary`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const getAllClients = async () => {
  const { empId } = getUserData();

  const url = `${backendEndpoint}/v1/${empId}/clients`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getAllInvoices = async (project_id, month, year) => {
  const { empId } = getUserData();

  const params = new URLSearchParams();
  if (project_id) params.append("project_id", project_id);
  if (month) params.append("month", month);
  if (year) params.append("year", year);

  const url = `${backendEndpoint}/v1/${empId}/invoices${
    params.toString() ? "?" + params.toString() : ""
  }`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const getProjectModules = async (project_id) => {
  const { empId } = getUserData();

  const url = `${backendEndpoint}/v1/${empId}/project/${project_id}/modules`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const getProjectsSummary = async (start_date, end_date, status) => {
  const { empId } = getUserData();

  let url = `${backendEndpoint}/v1/${empId}/projects_summary`;
  const params = new URLSearchParams();
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);
  if (status) params.append("status", status);

  if (params.toString()) {
    url += `?${params.toString()}`;
  }
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const getEmployeesSummaryByProject = async (
  project_id,
  start_date,
  end_date,
) => {
  const { empId } = getUserData();

  let url = `${backendEndpoint}/v1/${empId}/employees_summary_by_project`;
  const params = new URLSearchParams();
  if (project_id) params.append("project_id", project_id);
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);

  if (params.toString()) {
    url += `?${params.toString()}`;
  }
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getBirthdays = async () => {
  const { empId } = getUserData();
  const url = `${backendEndpoint}/v1/${empId}/birthdays`;

  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const getWorkAnniversaries = async () => {
  const { empId } = getUserData();
  const url = `${backendEndpoint}/v1/${empId}/anniversaries`;

  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const getTotalEmployeesCount = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/employees_count`;

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const getEvents = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/events`;

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const addEvent = async(eventObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/event`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        event: eventObj.eventName,
        date: eventObj.eventDate,
      }),
    };
    const response =await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const sendEmail = async(obj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/send_email`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(obj),
    };
    const response =await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const addWorkLog = async(workLogObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/worklog`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(workLogObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const addEmailTemplate = async(emailObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/email_template`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(
        emailObj
      ),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const updateEmailTemplate = async(emailObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/email_template/${emailObj.id}`;
    const options = {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(emailObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const getEmailTemplates = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/email_templates`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};
export const detailEmailTemplate = async (template_id) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/email_template/${template_id}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};
export const deleteEmailTemplate = async (template_id) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/email_template/${template_id}`;

    const options = {
      method: "DELETE",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteUserAssignedToProject = async (userData) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/projects/${userData.project_id}/employee/${userData.id}`;
    const options = {
      method: "DELETE",
      headers: getHeaders(),
      body: JSON.stringify(userData),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const addProject = async(projectObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/project`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(projectObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const addTenantInvoiceData = async(data) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/tenant_invoice_details`;
    const response = await axios.post(url, data, {
      headers: getHeaders("multipart/form-data"),
    });
    return response;
  } catch (err) {
    throw err;
  }
};
export const addClient = async(clientObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/client`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(clientObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getInvoiceSequenceNumber = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/invoice_number`;

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const updateInvoiceSequenceNumber = async(invoiceObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/invoice_number`;
    const options = {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(invoiceObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const updateClient = async(clientObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/client/${clientObj.id}`;
    const options = {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(clientObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const strikeOffInvoice = async(strikeOffObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/invoice/strike-off`;
    const options = {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(strikeOffObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const updateTenantInvoiceData = async(data) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/tenant_invoice_details`;
    const response = await axios.patch(url, data, {
      headers: getHeaders("multipart/form-data"),
    });
    return response;
  } catch (err) {
    throw err;
  }
};
export const assignUserToProject = async(userObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/projects/employee`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(userObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const updateProject = async(projectObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/project/${projectObj.id}`;
    const options = {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(projectObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const updateUserAssignedToProject = async(userProjectObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/projects/${userProjectObj.project_id}/employee`;
    const options = {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(userProjectObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const updateWorklog = async(workLogObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/update_worklog/${workLogObj.id}`;
    const options = {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(workLogObj),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const updateEmployeeProjectStatus = async(statusObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/projects/${statusObj.project_id}/employee/status`;
    const options = {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify({
        id: statusObj.id,
        status: statusObj.status,
        released_at: statusObj.released_at,
      }),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteEvent = async (eachEvent) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/event/${eachEvent.id}?event=${eachEvent.event}`;

    const options = {
      method: "DELETE",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const deleteProject = async (id, force_delete = false) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/project/${id}`;

    const options = {
      method: "DELETE",
      headers: getHeaders(),
      body: JSON.stringify({ force_delete }),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const deleteClient = async (id) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/client/${id}`;

    const options = {
      method: "DELETE",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const deleteWorkLog = async (id) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/worklog/${id}`;

    const options = {
      method: "DELETE",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

//Holidays fetch api calls

export const getHolidays = async () => {
  try {
    const { empId } = getUserData();

    const url = `${backendEndpoint}/v1/${empId}/holidays`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};
export const getDynamicVariables = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/dynamic_variables`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};
export const getProjectDetails = async (projectId) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/projects/${projectId}/details`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};
export const getMonthlyWorkLogs = async (year, empid, project_id) => {
  const { empId } = getUserData();

  const params = new URLSearchParams();
  if (project_id) params.append("project_id", project_id);
  if (empid) params.append("emp_id", empid);
  if (year) params.append("year", year);

  const url = `${backendEndpoint}/v1/${empId}/all_month_summary${
    params.toString() ? "?" + params.toString() : ""
  }`;
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const addHoliday = async (addHolidayObj) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/holiday`;

    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        occasion: addHolidayObj.holidayName,
        date: addHolidayObj.eventDate,
      }),
    };

    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteHoliday = async (eachHoliday) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/holiday/${eachHoliday.id}`;

    const options = {
      method: "DELETE",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

//Profile API Fetch calls

export const getProfile = async () => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/employee`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch {}
};
export const getDownloadAllInvoices = async (project_id) => {
  const { empId } = getUserData();
  try {
    let url = `${backendEndpoint}/v1/${empId}/download_all_invoices`;
    const params = new URLSearchParams();
        if (project_id) params.append("project_id", project_id);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch {}
};

// aadharFile/panFile are optional — pass either (or both) to attach an Aadhar/PAN
// PDF alongside the rest of the update. No separate documents endpoint: they're
// just two more optional columns on this same record.
export const updateEmployee = async (data, { aadharFile, panFile } = {}) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/update_employee`;

    const fields = {
      mobile_number: data.mobileNumber,
      date_of_birth: data.dob,
      blood_group: data.bloodGroup,
      emergency_contact_name: data.emergencycontactName,
      emergency_contact_number: data.emergencyContactNumber,
      address: data.location,
    };

    let options;
    if (aadharFile || panFile) {
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null) formData.append(key, value);
      });
      if (aadharFile) formData.append("aadhar_card", aadharFile, aadharFile.name);
      if (panFile) formData.append("pan_card", panFile, panFile.name);

      const headers = getHeaders();
      delete headers["Content-Type"];
      options = { method: "PUT", headers, body: formData };
    } else {
      options = {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(fields),
      };
    }

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};
export const UpdateInvoiceData = async (data) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/invoices/${data.id}`;

    const options = {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(data),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const updateProfilePicture = async (formData, boundary) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/profile_picture`;
    const response = await axios.put(url, formData, {
      headers: getHeaders(`multipart/form-data ; boundary = ${boundary}`),
    });

    return response;
  } catch {}
};

export const updateCoverPicture = async (formData, boundary) => {
  try {
    const { empId } = getUserData();

    const url = `${backendEndpoint}/v1/${empId}/cover_picture`;
    const response = await axios.put(url, formData, {
      headers: getHeaders(`multipart/form-data ; boundary = ${boundary}`),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteProfilePicture = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/profile_picture`;

    const options = {
      method: "DELETE",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteCoverPicture = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/cover_picture`;
    const options = {
      method: "DELETE",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

//leave API Fetch calls

export const getUserLeavesHistory = async (queryArg) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/leaves?start_month=${queryArg.startMonth}&end_month=${queryArg.endMonth}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getLeavesSummary = async (start_month,end_month) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/leaves_summary?start_month=${start_month}&end_month=${end_month}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (error) {}
};

export const applyLeave = async (queryArg) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/leave`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(queryArg),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

//Skils API Fetch calls

export const getSkills = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/skills`;

    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const addSkill = async (skillArray) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/skill`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(skillArray),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const fetchEmployeeSkills = async (empId) => {
  try {
    const url = `${backendEndpoint}/v1/${empId}/skills`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return await response.json();
  } catch (err) {
    throw err;
  }
};

export const updateSkill = async (EditedskillData) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/skill`;

    const options = {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(EditedskillData),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const deleteUserSkill = async (skillName) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/skill`;

    const options = {
      method: "DELETE",
      headers: getHeaders(),
      body: JSON.stringify({
        skill_name: skillName,
      }),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

//Other API fetch calls

//1. certificates API calls

export const getUserCertificates = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/certificates`;

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch {}
};
//2.reset password
export const resetUserPassword = async (resetObject) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/password`;
    const options = {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({
        current_password: resetObject.currentPassword,
        new_password: resetObject.newPassword,
      }),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

//ALL employees Component API Fetch calls
export const exportAllEmployees = async (exportType) => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };
    const url = `${backendEndpoint}/v1/${empId}/export_employees?status=${exportType}`;
    const response = await axios(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const exportExpenditures = async (queryArg) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/expenditure/export?start_month=${queryArg.start_month}&end_month=${queryArg.end_month}`;

    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };
    const response = await axios.get(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const exportLeaveSummary = async (queryArg) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/export_leaves_summary?start_month=${queryArg.start_month}&end_month=${queryArg.end_month}`;

    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };
    const response = await axios.get(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const exportLeaveHistory = async (queryArg) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/export_leaves_history?start_month=${queryArg.start_month}&end_month=${queryArg.end_month}`;

    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };
    const response = await axios.get(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const sampleFile = async () => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };

    const url = `${backendEndpoint}/v1/${empId}/export_employees/sample_file`;
    const response = await axios(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getAllActiveEmployees = async () => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/employees/active`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const getAllReleasedEmployees = async () => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/employees/released`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteUser = async (deleteUserIds) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/employee`;
    const options = {
      method: "DELETE",
      headers: getHeaders(),
      body: JSON.stringify({ employee_ids: deleteUserIds }),
    };

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};
export const deletePolicies = async (deletePolicyIds) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/policy`;
    const options = {
      method: "DELETE",
      headers: getHeaders(),
      body: JSON.stringify({ policy_ids: deletePolicyIds }),
    };

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const importEmployees = async (formData) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/import_employees`;
    const response = await axios.post(url, formData, {
      headers: getHeaders("multipart/form-data"),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

//Add employee component API fetch calls

export const addEmployee = async (data, certificatesList) => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "POST",
      body: JSON.stringify({
        first_name:
          data.firstName[0].toUpperCase() + data.firstName.substring(1),
        last_name: data.lastName[0].toUpperCase() + data.lastName.substring(1),
        id: data.id,
        mobile_number: data.mobileNumber,
        email: data.email,
        date_of_birth: data.dob,
        joining_date: data.joiningDate,
        qualifications: data.qualifications,
        designation: data.designation,
        department: data.department,
        uan_number: data.uan,
        address: data.location,
        blood_group: data.bloodGroup,
        gender: data.gender,
        password: data.password,
        role: data.role,
        salary: parseInt(data.salary),
        reporting_to: data.reportingTo,
        tenth: certificatesList["10th"],
        intermediate: certificatesList["Intermediate"],
        degree: certificatesList["Degree"],
        masters: certificatesList["PG"],
        original_degree: certificatesList["original Degree"],
        past_experience: data.pastExperience,
        personal_email:data.personalEmail
      }),
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/employee`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

// aadharFile/panFile are optional — pass either (or both) to attach an Aadhar/PAN
// PDF alongside the rest of the update. No separate documents endpoint: they're
// just two more optional columns on this same record.
export const editEmployee = async (data, updatedCertificates, { aadharFile, panFile } = {}) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/update_employee/all`;

    const fields = {
      first_name: data.firstName,
      last_name: data.lastName,
      id: data.id,
      email: data.email?.trim(),
      role: data.role,
      gender: data.gender,
      date_of_birth: data.dob,
      joining_date: data.joiningDate,
      qualifications: data.qualifications,
      department: data.department,
      mobile_number: data.mobileNumber,
      blood_group: data.bloodGroup,
      designation: data.designation,
      salary: data.salary !== undefined ? parseInt(data.salary) : undefined,
      address: data.location,
      bank_name: data.bankName,
      bank_account_number: data.bankAccountNumber,
      ifsc: data.ifsc,
      pan_number: data.panNumber,
      pf_number: data.pfNumber,
      uan_number: data.uanNumber,
      health_card_number: data.healthCardNumber,
      employement_status: data.employeeStatus,
      employee_project_status: data.employeeProjectStatus,
      reporting_to: data.reportingTo,
      tenth: updatedCertificates.tenth,
      intermediate: updatedCertificates.intermediate,
      degree: updatedCertificates.degree,
      release_date: data.releaseDate,
      released_type: data.releasedType,
      personal_email: data.personalEmail,
      past_experience: data.pastExperience,
      masters: updatedCertificates.masters,
      original_degree: updatedCertificates.originalDegree,
    };

    let options;
    if (aadharFile || panFile) {
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null) formData.append(key, value);
      });
      if (aadharFile) formData.append("aadhar_card", aadharFile, aadharFile.name);
      if (panFile) formData.append("pan_card", panFile, panFile.name);

      const headers = getHeaders();
      delete headers["Content-Type"];
      options = { method: "PUT", headers, body: formData };
    } else {
      options = {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(fields),
      };
    }

    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

//Leave Management component API calls

export const getLeavesCount = async () => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/leaves_count`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const getLeads = async () => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/leads`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

// Powers the Add/Edit Employee "Reporting To" role-then-email picker:
// [{ email, role }] for Super Admin / Admin / HR / Lead.
export const getReportingCandidates = async () => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/reporting_candidates`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const getHolidaysByYear = async (holidaysYear) => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/year_holidays?year=${holidaysYear}`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

//Leave request component API calls

export const getPendingLeaves = async ({start_month,end_month}) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/pending_leaves?start_month=${start_month}&end_month=${end_month}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateLeave = async (leaveStatus, LeaveId, rejectedReason) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/leave/${LeaveId}`;

    const options = {
      method: "PUT",
      body: JSON.stringify({
        status: leaveStatus,
        rejected_reason: rejectedReason,
      }),
      headers: getHeaders(),
    };

    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getEmployeesLeavesHistory = async (
  formattedStartMonth,
  formattedEndMonth,
) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/leaves/all?start_month=${formattedStartMonth}&end_month=${formattedEndMonth}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const exportPayroll = async (formattedDate) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/payroll?current_date=${formattedDate}`;

    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };
    const response = await axios.get(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const exportLeaves = async () => {
  try {
    const { empId } = getUserData();

    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };
    const url = `${backendEndpoint}/v1/${empId}/export_leaves`;

    const response = await axios(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

//salary management Api calls

// Active-employee listing for Salary Management (name/email/designation/
// salary only) — gated by the Salary Management module's own permission on
export const getSalaryEmployees = async () => {
  try {
    const { empId } = getUserData();
    const response = await axios.get(
      `${backendEndpoint}/v1/${empId}/salary/employees`,
      { headers: getHeaders() },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const getALlPayslips = async (formattedMonth) => {
  try {
    const { empId } = getUserData();

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/payslips?year_month=${formattedMonth}`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const getRecentPayslips = async (employeeId) => {
  try {
    // const { empId } = getUserData();

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${employeeId}/recent_payslips`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const getRecentPayslipPdfFiles = async (employeeId) => {
  try {
    // const { empId } = getUserData();

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${employeeId}/payslip/files`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const getEmployeePayslipPdfFile = async (
  selectedYear,
  selectedMonth,
  employeeId,
) => {
  try {
    const { empId } = getUserData();

    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    // First path segment must be the caller's own id (server-validated against
    // the JWT); the second is the target employee whose payslip is fetched —
    // same value as the first when viewing your own payslip.
    const url = `${backendEndpoint}/v1/${empId}/payslip/${employeeId}/file?year_month=${selectedYear}-${selectedMonth}`;
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const importLeaves = async (formData) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/import_leaves`;
    const response = await axios.post(url, formData, {
      headers: getHeaders("multipart/form-data"),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const addIncentive = async (data) => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/employee_incentives`;
    const response = await axios.put(url, data, {
      headers: getHeaders(),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const addDeduction = async (data) => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/employee_deductions`;
    const response = await axios.put(url, data, {
      headers: getHeaders(),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

//Payslip component API Fetch calls

export const getUserPayslip = async (paramsEmployeeId, formattedStartMonth) => {
  try {
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(
      `${backendEndpoint}/v1/${paramsEmployeeId}/payslip?year_month=${formattedStartMonth}`,

      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const regenerateUserPayslip = async (data, formattedMonth) => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(data),
    };
    const response = await fetch(
      `${backendEndpoint}/v2/${empId}/update_payslip?year_month=${formattedMonth}`,

      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const auditFileExcelDownload = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/payroll/audit`;

    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };
    const response = await axios.get(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

//Resource Tracking paySlip component calls

export const exportResources = async (activeTrackingGadget) => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };

    const url = `${backendEndpoint}/v1/${empId}/export_resources?device_type=${activeTrackingGadget}&sample_file=False`;

    const response = await axios(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const downloadSampleResources = async (activeTrackingGadget) => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };

    const url = `${backendEndpoint}/v1/${empId}/export_resources?device_type=${activeTrackingGadget}&sample_file=True`;

    const response = await axios(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const exportInvoiceExcelTemplate = async (month,year) => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
      responseType: "blob",
    };

    const url = `${backendEndpoint}/v1/${empId}/export_invoice_excel?month=${month}&year=${year}`;

    const response = await axios(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const assignResource = async (resourceData) => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(resourceData),
    };
    const url = `${backendEndpoint}/v1/${empId}/resource/assign`;

    const response = await fetch(url, options);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getExcessOrReleasedResources = async () => {
  try {
    const { empId } = getUserData();
    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const url = `${backendEndpoint}/v1/${empId}/resources/released`;

    const response = await axios(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const importResources = async (formData) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/import_resources`;
    const response = await axios.post(url, formData, {
      headers: getHeaders("multipart/form-data"),
    });
    return response;
  } catch (err) {
    throw err;
  }
};
export const importInvoiceTemplate = async (formData) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/import_invoice_excel`;
    const response = await axios.patch(url, formData, {
      headers: getHeaders("multipart/form-data"),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const getResource = async (activeTrackingGadget) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/resources?type=${activeTrackingGadget}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getResourceSummary = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/resources_summary`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const resourceTracking = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/resources_tracking`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const addResource = async (data) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/add_resources`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        employee_id: empId,
        assigned_date: data.allocatedDate,
        device_type: data.deviceType,
        serial_id: data.serialId,
        model: data.model,
        count: data.deviceType !== "LAN" ? 1 : data.totalAssigned,
        status: "Not Assigned",
      }),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const releaseResource = async (releaseData) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/release_resource`;

    // Making PUT request with releaseData and necessary headers
    const response = await axios.put(url, releaseData, {
      headers: getHeaders(),
    });

    return response;
  } catch (err) {
    console.error("Error in relaseResource:", err);
    throw err;
  }
};

export const getWorkRequests = async () => {
  try {
    const { empId } = getUserData();

    const url = `${backendEndpoint}/v1/${empId}/pending_extra_works`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};
export const exportWorklogs = async (proj_id, emp_id, start_date, end_date) => {
  try {
    const { empId } = getUserData();

    let url = `${backendEndpoint}/v1/${empId}/worklog/export_excel/${proj_id}`;

    const params = new URLSearchParams();
    if(emp_id) params.append("employee_id", emp_id);
    if (start_date) params.append("start_date", start_date);
    if (end_date) params.append("end_date", end_date);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const exportEmployeeRevenue = async (project_id, start_month, end_month, currency) => {
  try {
    const { empId } = getUserData();

    let url = `${backendEndpoint}/v1/${empId}/employee_revenue/export`;

    const params = new URLSearchParams();
    if (project_id) params.append("project_id", project_id);
    if (start_month) params.append("start_month", start_month);
    if (end_month) params.append("end_month", end_month);
    if (currency) params.append("currency", currency);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const exportProjectRevenue = async (project_id, start_month, end_month, currency) => {
  try {
    const { empId } = getUserData();

    let url = `${backendEndpoint}/v1/${empId}/project_revenue/export`;

    const params = new URLSearchParams();
    if (project_id) params.append("project_id", project_id);
    if (start_month) params.append("start_month", start_month);
    if (end_month) params.append("end_month", end_month);
    if (currency) params.append("currency", currency);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const exportWorklogsZip = async (project_ids, start_date, end_date) => {
  try {
    const { empId } = getUserData();

    let url = `${backendEndpoint}/v1/${empId}/worklog/export_zip`;

    const params = new URLSearchParams();
    if (start_date) params.append("start_date", start_date);
    if (end_date) params.append("end_date", end_date);
    if (project_ids) params.append("project_ids", project_ids);


    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateExtraWork = async (workId, status, rejectedReason) => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/extra_work/${workId}`;
    const options = {
      method: "PUT",
      body: JSON.stringify({
        status: status,
        rejected_reason: rejectedReason,
      }),
      headers: getHeaders(),
    };

    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getAllWorksByMonth = async (formattedMonth) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/extra_works/all?year_month=${formattedMonth}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getUserWorksByMonth = async (formattedStartMonth) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/extra_works?year_month=${formattedStartMonth}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const applyUserExtraWork = async (data) => {
  const { empId } = getUserData();
  try {
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    };

    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/extra_work`,
      options,
    );
    return response;
  } catch (err) {
    throw err;
  }
};

//expenditure API Fetch calls

export const getExpenditures = async (queryArg) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/expenditures?start_month=${queryArg.start_month}&end_month=${queryArg.end_month}`;

    const options = {
      method: "GET",
      headers: getHeaders(),
    };

    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const addExpenditure = async (formData, boundary) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/expenditure`;
    const response = await axios.post(url, formData, {
      headers: getHeaders(`multipart/form-data ; boundary = ${boundary}`),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const getExpenditureBillUrls = async (queryArg) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/expenditure/urls?start_month=${queryArg.start_month}&end_month=${queryArg.end_month}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteExpenditure = async (expenditureIds) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/expenditure`;
    const options = {
      method: "DELETE",
      headers: getHeaders(),
      body: JSON.stringify({ expenditure_ids: expenditureIds }),
    };

    const response = await fetch(url, options);

    return response;
  } catch (err) {
    throw err;
  }
};

export const getOrganizationSettingsInfo = async () => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/basic_info`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateOrganizationSettingsInfo = async (data) => {
  const { empId } = getUserData();

  try {
    const url = `${backendEndpoint}/v1/${empId}/basic_info`;
    const options = {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(data),
    };

    const response = await fetch(url, options);
    // const response = await axios.put(url, formData, {
    //   headers: getHeaders(`multipart/form-data ; boundary = ${boundary}`),
    // });
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateOrganizationLogo = async (formData, boundary) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/tenant_logo`;
    const response = await axios.put(url, formData, {
      headers: getHeaders(`multipart/form-data ; boundary = ${boundary}`),
    });
    return response;
  } catch {}
};
export const uploadCompanyLogo = async (formData, boundary) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/company_logo`;
    const response = await axios.post(url, formData, {
      headers: getHeaders(`multipart/form-data ; boundary = ${boundary}`),
    });
    return response;
  } catch {}
};

export const getTenantsData = async () => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/tenants`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const addTenantData = async (data) => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/tenant`;
    const options = {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateTenantData = async (data) => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/tenant`;
    const options = {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(data),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteTenant = async (tenantId) => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/tenant`;
    const options = {
      method: "DELETE",
      headers: getHeaders(),
      body: JSON.stringify({ tenant_id: tenantId }),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const tenantDataBeforeLogin = async (domain) => {
  try {
    const url = `${backendEndpoint}/v1/tenant/${domain}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const uploadPolicyPdf = async (policyObj, boundary) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/policy`;

    const response = await axios.post(url, policyObj, {
      headers: getHeaders(`multipart/form-data ; boundary = ${boundary}`),
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const updatePolicyPdf = async (policyObj, boundary) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/policy`;

    const response = await axios.patch(url, policyObj, {
      headers: getHeaders(`multipart/form-data ; boundary = ${boundary}`),
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const getPolicyPdfList = async (policyObj, boundary) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/policies`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);

    return response;
  } catch (error) {
    throw error;
  }
};

export const getPolicyRequests = async (status) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/policy/requests?status=${status}`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    throw error;
  }
};

export const updatePolicyRequest = async (requestId, status, rejectedReason) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/policy/request/${requestId}`;
    const options = {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({
        status,
        rejected_reason: rejectedReason,
      }),
    };
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getOrganizationLeads = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/leads`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getEmployeeLead = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/employee/lead`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAdmins = async () => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/admins`;
    const options = {
      method: "GET",
      headers: getHeaders(),
    };
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    throw error;
  }
};

// Form 16 API calls

export const createForm16Draft = async ({ form16EmployeeId, financialYear, file, isNewSession }) => {
  try {
    const { empId } = getUserData();
    const formData = new FormData();
    formData.append("form_16_employee_id", form16EmployeeId);
    formData.append("financial_year", financialYear);
    formData.append("is_new_session", String(isNewSession));
    formData.append("file", file);
    const response = await axios.post(
      `${backendEndpoint}/v1/${empId}/form16/draft`,
      formData,
      { headers: getHeaders("multipart/form-data") },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteForm16Draft = async (form16Id) => {
  try {
    const { empId } = getUserData();
    const response = await axios.delete(
      `${backendEndpoint}/v1/${empId}/form16/draft/${form16Id}`,
      { headers: getHeaders() },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const publishForm16Drafts = async () => {
  try {
    const { empId } = getUserData();
    const response = await axios.post(
      `${backendEndpoint}/v1/${empId}/form16/publish`,
      {},
      { headers: getHeaders() },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const cancelForm16Import = async () => {
  try {
    const { empId } = getUserData();
    const response = await axios.delete(
      `${backendEndpoint}/v1/${empId}/form16/drafts`,
      { headers: getHeaders() },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const downloadForm16File = async (form16Id) => {
  const { empId } = getUserData();
  try {
    const response = await fetch(
      `${backendEndpoint}/v1/${empId}/form16/${form16Id}/download`,
      { method: "GET", headers: getHeaders() }
    );
    return response;
  } catch {}
};

export const downloadAllForm16 = async (financialYear) => {
  const { empId } = getUserData();
  try {
    const url = `${backendEndpoint}/v1/${empId}/form16/download?financial_year=${encodeURIComponent(financialYear)}`;
    const response = await fetch(url, { method: "GET", headers: getHeaders() });
    return response;
  } catch {}
};

export const getForm16Records = async (financialYear) => {
  try {
    const { empId } = getUserData();
    const response = await axios.get(
      `${backendEndpoint}/v1/${empId}/form16?financial_year=${encodeURIComponent(financialYear)}`,
      { headers: getHeaders() },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateForm16Record = async (form16Id, file) => {
  try {
    const { empId } = getUserData();
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.put(
      `${backendEndpoint}/v1/${empId}/form16/${form16Id}`,
      formData,
      { headers: getHeaders("multipart/form-data") },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteForm16Record = async (form16Id) => {
  try {
    const { empId } = getUserData();
    const response = await axios.delete(
      `${backendEndpoint}/v1/${empId}/form16/${form16Id}`,
      { headers: getHeaders() },
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateAssignedLead = async (assignedLead) => {
  try {
    const { empId } = getUserData();
    const url = `${backendEndpoint}/v1/${empId}/lead`;
    const options = {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({ lead_email: assignedLead }),
    };
    const response = await fetch(url, options);

    return response;
  } catch (error) {
    throw error;
  }
};

/* ── Hiring Jobs API calls ── */

const HIRING_DATE_FILTER_MAP = {
  LAST_7:  "LAST_7_DAYS",
  LAST_30: "LAST_30_DAYS",
};

export const getHiringJobs = async ({
  status = "OPEN",
  page = 1,
  limit = 100,
  job_title,
  posted_filter,
  from_date,
  to_date,
} = {}) => {
  try {
    const params = new URLSearchParams();
    params.append("status", status);
    params.append("page", String(page));
    params.append("limit", String(Math.min(Math.max(limit, 1), 100)));
    if (job_title) params.append("job_title", job_title);
    if (posted_filter && posted_filter !== "ALL") {
      params.append("posted_filter", HIRING_DATE_FILTER_MAP[posted_filter] || posted_filter);
    }
    if (from_date) params.append("from_date", from_date);
    if (to_date) params.append("to_date", to_date);

    const url = `${backendEndpoint}/v1/hiring/jobs?${params.toString()}`;
    const response = await fetch(url, { method: "GET", headers: getHeaders() });
    return response;
  } catch (err) {
    throw err;
  }
};

export const getHiringOverview = async ({ from_date, to_date } = {}) => {
  try {
    const params = new URLSearchParams();
    if (from_date) params.append("from_date", from_date);
    if (to_date) params.append("to_date", to_date);
    const url = `${backendEndpoint}/v1/hiring/overview?${params.toString()}`;
    const response = await fetch(url, { method: "GET", headers: getHeaders() });
    return response;
  } catch (err) {
    throw err;
  }
};

export const createHiringJob = async (jobData) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs`;
    const response = await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(jobData),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const getHiringJobById = async (jobId) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}`;
    const response = await fetch(url, { method: "GET", headers: getHeaders() });
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateHiringJob = async (jobId, updateData) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(updateData),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateHiringJobStatus = async (jobId, status) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/status`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify({ status }),
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteHiringJob = async (jobId) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}`;
    const response = await fetch(url, { method: "DELETE", headers: getHeaders() });
    return response;
  } catch (err) {
    throw err;
  }
};

/* ── Hiring Applications API calls ── */

export const getHiringApplications = async (jobId, { status, page = 1, limit = 50 } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("limit", String(Math.min(Math.max(limit, 1), 100)));
    if (status) params.append("status", status);
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/applications?${params.toString()}`;
    return await fetch(url, { method: "GET", headers: getHeaders() });
  } catch (err) {
    throw err;
  }
};

export const getMyReferrals = async () => {
  try {
    const url = `${backendEndpoint}/v1/hiring/applications/my-referrals`;
    return await fetch(url, { method: "GET", headers: getHeaders() });
  } catch (err) {
    throw err;
  }
};

// Admin/HR only — every referral made org-wide, not just the current user's own.
export const getAllReferrals = async () => {
  try {
    const url = `${backendEndpoint}/v1/hiring/applications/referrals`;
    return await fetch(url, { method: "GET", headers: getHeaders() });
  } catch (err) {
    throw err;
  }
};

export const createHiringApplication = async (jobId, formData) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/applications`;
    // Content-Type must NOT be set manually — browser sets it with the boundary for multipart
    const headers = getHeaders();
    delete headers["Content-Type"];
    return await fetch(url, { method: "POST", headers, body: formData });
  } catch (err) {
    throw err;
  }
};

// Used only when a new resume file is attached — a file requires multipart/form-data.
export const updateHiringApplication = async (jobId, applicationId, formData) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/applications/${applicationId}`;
    const headers = getHeaders();
    delete headers["Content-Type"];
    return await fetch(url, { method: "PATCH", headers, body: formData });
  } catch (err) {
    throw err;
  }
};

// Used when there's no resume file to upload — plain JSON body instead of multipart.
export const updateHiringApplicationJson = async (jobId, applicationId, payload) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/applications/${applicationId}`;
    return await fetch(url, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw err;
  }
};

export const updateHiringApplicationStatus = async (jobId, applicationId, status) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/applications/${applicationId}/status`;
    return await fetch(url, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify({ status }),
    });
  } catch (err) {
    throw err;
  }
};

export const deleteHiringApplication = async (jobId, applicationId) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/applications/${applicationId}`;
    return await fetch(url, { method: "DELETE", headers: getHeaders() });
  } catch (err) {
    throw err;
  }
};

/* ── Unified Resume Import API ─────────────────────────────── */
/*
 * Phase 1 — Start import  (multipart/form-data, files=[file1, file2, ...])
 *   POST /v1/hiring/jobs/{job_id}/resume-import
 *   → { session_id, status: "PROCESSING", total_files }
 *
 * Phase 2 — Poll status
 *   GET /v1/hiring/jobs/{job_id}/resume-import/{session_id}/status
 *   → Poll every 2–3s until status === "COMPLETED"
 *   → { summary, parsed_candidates, failed_files, ... }
 *
 * Phase 3 — Save   (application/json)
 *   POST /v1/hiring/jobs/{job_id}/resume-import/save
 *   { session_id, candidates: [{ temp_id, ... }] }
 *   → Returns created application objects (409 if still PROCESSING)
 */

export const parseResumes = async (jobId, formData, signal) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume-import`;
    const headers = getHeaders();
    delete headers["Content-Type"];
    const opts = { method: "POST", headers, body: formData };
    if (signal) opts.signal = signal;
    return await fetch(url, opts);
  } catch (err) {
    throw err;
  }
};

export const getResumeImportStatus = async (jobId, sessionId, signal) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume-import/${sessionId}/status`;
    const opts = { method: "GET", headers: getHeaders() };
    if (signal) opts.signal = signal;
    return await fetch(url, opts);
  } catch (err) {
    throw err;
  }
};

export const saveResumes = async (jobId, payload) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume-import/save`;
    return await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw err;
  }
};

/*
 * ── Status-wise Resume Import Endpoints (for Needs Review / Duplicate / Failed tabs) ──
 *
 * After POST /resume-import completes, the backend MUST persist each parsed file with
 * import_status: READY | NEEDS_REVIEW | DUPLICATE | FAILED.
 * READY rows are auto-saved as applications (frontend also calls /save for READY).
 * The three tabs below fetch only non-saved pending import rows.
 *
 * GET  /v1/hiring/jobs/{job_id}/resume-import/candidates?status=NEEDS_REVIEW|DUPLICATE|FAILED&session_id=&page=&limit=
 *   → { status: true, data: { candidates: [...], total, page, limit } }
 *
 * GET  /v1/hiring/jobs/{job_id}/resume-import/summary
 *   → { status: true, data: { session_id, counts: { NEEDS_REVIEW, DUPLICATE, FAILED, READY } } }
 *
 * POST /v1/hiring/jobs/{job_id}/resume-import/candidates
 *   Body: { session_id, candidates: [{ temp_id, import_status, parsed_data, ... }], failed_files: [...] }
 *   → Persists pending import rows after parse (called by frontend right after import completes)
 *
 * POST /v1/hiring/jobs/{job_id}/resume-import/candidates/{import_candidate_id}/save
 *   Body: { session_id, name, email, phone, skills, experience, ... }
 *   → Saves a NEEDS_REVIEW candidate as application; removes from pending list
 *
 * DELETE /v1/hiring/jobs/{job_id}/resume-import/candidates/{import_candidate_id}?session_id=
 *   → Dismisses DUPLICATE / FAILED / NEEDS_REVIEW row from pending list
 */

export const getResumeImportSummary = async (jobId, { session_id } = {}) => {
  try {
    const params = new URLSearchParams();
    if (session_id) params.append("session_id", session_id);
    const qs = params.toString();
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume-import/summary${qs ? `?${qs}` : ""}`;
    return await fetch(url, { method: "GET", headers: getHeaders() });
  } catch (err) {
    throw err;
  }
};

export const getResumeImportCandidates = async (jobId, { status, session_id, page = 1, limit = 50 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    if (session_id) params.append("session_id", session_id);
    params.append("page", String(page));
    params.append("limit", String(Math.min(Math.max(limit, 1), 100)));
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume-import/candidates?${params.toString()}`;
    return await fetch(url, { method: "GET", headers: getHeaders() });
  } catch (err) {
    throw err;
  }
};

export const storeResumeImportCandidates = async (jobId, payload) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume-import/candidates`;
    return await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw err;
  }
};

export const saveResumeImportCandidate = async (jobId, importCandidateId, payload) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume-import/candidates/${importCandidateId}/save`;
    return await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw err;
  }
};

export const deleteResumeImportCandidate = async (jobId, importCandidateId, { session_id } = {}) => {
  try {
    const params = new URLSearchParams();
    if (session_id) params.append("session_id", session_id);
    const qs = params.toString();
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume-import/candidates/${importCandidateId}${qs ? `?${qs}` : ""}`;
    return await fetch(url, { method: "DELETE", headers: getHeaders() });
  } catch (err) {
    throw err;
  }
};

export const getResumeImportByStatus = async (jobId, statusSlug, { session_id } = {}) => {
  try {
    const params = new URLSearchParams();
    if (session_id) params.append("session_id", session_id);
    const qs = params.toString();
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume-import/${statusSlug}${qs ? `?${qs}` : ""}`;
    return await fetch(url, { method: "GET", headers: getHeaders() });
  } catch (err) {
    throw err;
  }
};

/* ── Legacy AI-Assisted Hiring API calls (deprecated — use parseResumes / saveResumes) ── */

export const parseHiringResume = async (jobId, formData) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/resume/parse`;
    const headers = getHeaders();
    delete headers["Content-Type"];
    return await fetch(url, { method: "POST", headers, body: formData });
  } catch (err) {
    throw err;
  }
};

export const createHiringCandidateFromParse = async (jobId, candidateData) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/candidate/create`;
    return await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(candidateData),
    });
  } catch (err) {
    throw err;
  }
};

export const bulkUploadHiringResumes = async (jobId, formData, signal) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/bulk-upload`;
    const headers = getHeaders();
    delete headers["Content-Type"];
    const opts = { method: "POST", headers, body: formData };
    if (signal) opts.signal = signal;
    return await fetch(url, opts);
  } catch (err) {
    throw err;
  }
};

export const bulkSaveHiringCandidates = async (jobId, payload) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/bulk-save`;
    return await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw err;
  }
};

export const getHiringCandidateRanking = async (jobId, { sort, limit = 100 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (sort) params.append("sort", sort);
    params.append("limit", String(Math.min(Math.max(limit, 1), 100)));
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/ranking?${params.toString()}`;
    return await fetch(url, { method: "GET", headers: getHeaders() });
  } catch (err) {
    throw err;
  }
};

export const shortlistTopHiringCandidates = async (jobId, count) => {
  try {
    const url = `${backendEndpoint}/v1/hiring/jobs/${jobId}/shortlist`;
    return await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ count }),
    });
  } catch (err) {
    throw err;
  }
};
